import React from 'react';
import { render } from '@react-email/render';
import { logger } from '../lib/logger';
import { useLanguageStore } from '../store/languageStore';
import AppointmentConfirmed from '../emails/AppointmentConfirmed';
import AppointmentRejected from '../emails/AppointmentRejected';
import AppointmentCancelled from '../emails/AppointmentCancelled';
import NewAppointmentRequest from '../emails/NewAppointmentRequest';
import WelcomeVisitor from '../emails/WelcomeVisitor';
import PaymentConfirmed from '../emails/PaymentConfirmed';
import PaymentApproved from '../emails/PaymentApproved';
import PaymentRejected from '../emails/PaymentRejected';
import LevelChanged from '../emails/LevelChanged';

interface EmailTemplate {
  subject: string;
  html: string;
  text: string;
}

interface AppointmentConfirmedData {
  recipientName: string;
  otherPartyName: string;
  date: string;
  time: string;
  location?: string;
  type?: 'in-person' | 'virtual' | 'hybrid';
  isExhibitor?: boolean;
}

interface AppointmentRejectedData {
  visitorName: string;
  exhibitorName: string;
  date: string;
  time: string;
}

interface AppointmentCancelledData {
  recipientName: string;
  otherPartyName: string;
  date: string;
  time: string;
  cancelledBy: 'visitor' | 'exhibitor';
}

interface NewAppointmentRequestData {
  exhibitorName: string;
  visitorName: string;
  visitorEmail: string;
  visitorCompany?: string;
  visitorPosition?: string;
  date: string;
  time: string;
  message?: string;
}

interface WelcomeVisitorData {
  name: string;
  email: string;
  level: 'free' | 'premium' | 'vip';
  includePaymentInstructions?: boolean;
}

interface PaymentApprovedData {
  name: string;
  amount: string;
  level: 'premium' | 'vip';
}

interface PaymentRejectedData {
  name: string;
  amount: string;
  reason: string;
}

interface LevelChangedData {
  name: string;
  oldLevel: string;
  newLevel: string;
}

interface PaymentConfirmedData {
  name: string;
  amount: string;
  receiptNumber: string;
  date: string;
  level: 'premium' | 'vip';
}

function t(key: string): string {
  return useLanguageStore.getState().translateText(key);
}

function layoutTranslations() {
  return {
    headerSubtitle: t('email.header_subtitle'),
    footerTitle: t('email.footer_title'),
    footerLocation: t('email.footer_location'),
    footerUnsubscribe: t('email.footer_unsubscribe'),
  };
}

function renderTemplate(element: React.ReactElement): { html: string; text: string } {
  const html = render(element);
  const text = render(element, { plainText: true });
  return { html, text };
}

class ReactEmailService {
  renderAppointmentConfirmed(data: AppointmentConfirmedData): EmailTemplate {
    const translations = {
      title: t('email.confirmed_title'),
      greeting: t('email.greeting'),
      bodyVisitor: t('email.confirmed_body_visitor'),
      bodyExhibitor: t('email.confirmed_body_exhibitor'),
      labelDate: t('email.label_date'),
      labelTime: t('email.label_time'),
      labelType: t('email.label_type'),
      labelLocation: t('email.label_location'),
      reminder: t('email.reminder_24h'),
      ctaText: t('email.view_my_appointments'),
      signOff: t('email.sign_off_soon'),
      teamSignature: t('email.team_signature'),
      typeInPerson: t('slot_type.in_person'),
      typeVirtual: t('slot_type.virtual'),
      typeHybrid: t('slot_type.hybrid'),
      layout: layoutTranslations(),
    };

    const { html, text } = renderTemplate(
      React.createElement(AppointmentConfirmed, { ...data, translations })
    );
    const prefix = data.isExhibitor
      ? t('email.subject_appointment_confirmed_prefix')
      : t('email.subject_appointment_confirmed');
    return {
      subject: `${prefix} - ${data.otherPartyName}`,
      html,
      text,
    };
  }

  renderAppointmentRejected(data: AppointmentRejectedData): EmailTemplate {
    const translations = {
      title: t('email.rejected_title'),
      greeting: t('email.greeting'),
      body: t('email.rejected_body'),
      labelRequestedDate: t('email.label_requested_date'),
      labelRequestedTime: t('email.label_requested_time'),
      labelExhibitor: t('email.label_exhibitor'),
      action: t('email.rejected_action'),
      ctaText: t('email.view_available_exhibitors'),
      signOff: t('email.sign_off_regards'),
      teamSignature: t('email.team_signature'),
      layout: layoutTranslations(),
    };

    const { html, text } = renderTemplate(
      React.createElement(AppointmentRejected, { ...data, translations })
    );
    return {
      subject: `${t('email.subject_appointment_rejected')} - ${data.exhibitorName}`,
      html,
      text,
    };
  }

  renderAppointmentCancelled(data: AppointmentCancelledData): EmailTemplate {
    const translations = {
      title: t('email.cancelled_title'),
      greeting: t('email.greeting'),
      body: t('email.cancelled_body'),
      labelDate: t('email.label_date'),
      labelTime: t('email.label_time'),
      labelWith: t('email.label_with'),
      reschedule: t('email.cancelled_reschedule'),
      ctaText: t('email.view_my_appointments'),
      signOff: t('email.sign_off_regards'),
      teamSignature: t('email.team_signature'),
      cancelledByVisitor: t('email.cancelled_by_visitor'),
      cancelledByExhibitor: t('email.cancelled_by_exhibitor'),
      layout: layoutTranslations(),
    };

    const { html, text } = renderTemplate(
      React.createElement(AppointmentCancelled, { ...data, translations })
    );
    return {
      subject: `${t('email.subject_appointment_cancelled')} ${data.otherPartyName}`,
      html,
      text,
    };
  }

  renderNewAppointmentRequest(data: NewAppointmentRequestData): EmailTemplate {
    const translations = {
      title: t('email.new_request_title'),
      greeting: t('email.greeting'),
      body: t('email.new_request_body'),
      visitorInformation: t('email.visitor_information'),
      labelName: t('email.label_name'),
      labelEmail: t('email.label_email'),
      labelPosition: t('email.label_position'),
      labelCompany: t('email.label_company'),
      requestedSlot: t('email.requested_slot'),
      labelDate: t('email.label_date'),
      labelTime: t('email.label_time'),
      visitorMessage: t('email.visitor_message'),
      action: t('email.new_request_action'),
      ctaText: t('email.manage_requests'),
      signOff: t('email.sign_off_regards'),
      teamSignature: t('email.team_signature'),
      layout: layoutTranslations(),
    };

    const { html, text } = renderTemplate(
      React.createElement(NewAppointmentRequest, { ...data, translations })
    );
    return {
      subject: `${t('email.subject_new_request')} ${data.visitorName}`,
      html,
      text,
    };
  }

  renderWelcomeVisitor(data: WelcomeVisitorData): EmailTemplate {
    const translations = {
      title: t('email.welcome_title'),
      greeting: t('email.greeting'),
      body: t('email.welcome_body'),
      summary: t('email.summary'),
      labelEmail: t('email.label_email'),
      labelLevel: t('email.label_level'),
      statusConfirmed: t('email.status_confirmed'),
      paymentInstructionsTitle: t('email.payment_instructions_title'),
      paymentInstructionsBody: t('email.payment_instructions_body'),
      eventInformation: t('email.event_information'),
      eventDates: t('email.event_dates'),
      eventLocation: t('email.event_location'),
      eventHours: t('email.event_hours'),
      ctaText: t('email.access_dashboard'),
      keepForConfirmation: t('email.keep_for_confirmation'),
      signOff: t('email.sign_off_thanks'),
      teamSignature: t('email.team_signature'),
      levelFree: t('visitor.level_free'),
      levelPremium: t('visitor.level_premium'),
      levelVip: t('visitor.level_vip'),
      layout: layoutTranslations(),
    };

    const { html, text } = renderTemplate(
      React.createElement(WelcomeVisitor, { ...data, translations })
    );
    const levelLabel = data.level === 'vip' ? 'VIP' : data.level === 'premium' ? 'Premium' : '';
    const suffix = levelLabel ? ` - ${t('email.subject_welcome_suffix')} ${levelLabel}` : '';
    return {
      subject: `${t('email.subject_welcome')}${suffix}`,
      html,
      text,
    };
  }

  renderPaymentConfirmed(data: PaymentConfirmedData): EmailTemplate {
    const translations = {
      title: t('email.payment_confirmed_title'),
      greeting: t('email.greeting'),
      body: t('email.payment_confirmed_body'),
      labelAmount: t('email.label_amount'),
      labelReceipt: t('email.label_receipt'),
      labelDate: t('email.label_date'),
      labelLevel: t('email.label_level'),
      access: t('email.payment_confirmed_access'),
      ctaText: t('email.access_dashboard'),
      keepForPayment: t('email.keep_for_payment'),
      signOff: t('email.sign_off_thanks'),
      teamSignature: t('email.team_signature'),
      layout: layoutTranslations(),
    };

    const { html, text } = renderTemplate(
      React.createElement(PaymentConfirmed, { ...data, translations })
    );
    return {
      subject: t('email.subject_payment_confirmed'),
      html,
      text,
    };
  }

  renderPaymentApproved(data: PaymentApprovedData): EmailTemplate {
    const translations = {
      title: t('email.payment_approved_title'),
      greeting: t('email.greeting'),
      body: t('email.payment_approved_body'),
      labelAmount: t('email.label_amount'),
      labelLevel: t('email.label_level'),
      badgeActive: t('email.payment_approved_badge_active'),
      ctaText: t('email.access_dashboard'),
      signOff: t('email.sign_off_thanks'),
      teamSignature: t('email.team_signature'),
      layout: layoutTranslations(),
    };

    const { html, text } = renderTemplate(
      React.createElement(PaymentApproved, { ...data, translations })
    );
    return {
      subject: t('email.subject_payment_approved'),
      html,
      text,
    };
  }

  renderPaymentRejected(data: PaymentRejectedData): EmailTemplate {
    const translations = {
      title: t('email.payment_rejected_title'),
      greeting: t('email.greeting'),
      body: t('email.payment_rejected_body'),
      labelAmount: t('email.label_amount'),
      reasonLabel: t('email.payment_rejected_reason'),
      actionRetry: t('email.payment_rejected_action'),
      ctaText: t('email.payment_rejected_retry'),
      contactSupport: t('email.contact_support'),
      signOff: t('email.sign_off_regards'),
      teamSignature: t('email.team_signature'),
      layout: layoutTranslations(),
    };

    const { html, text } = renderTemplate(
      React.createElement(PaymentRejected, { ...data, translations })
    );
    return {
      subject: t('email.subject_payment_rejected'),
      html,
      text,
    };
  }

  renderLevelChanged(data: LevelChangedData): EmailTemplate {
    const translations = {
      title: t('email.level_changed_title'),
      greeting: t('email.greeting'),
      bodyUpgrade: t('email.level_changed_body_upgrade'),
      bodyDowngrade: t('email.level_changed_body_downgrade'),
      labelOldLevel: t('email.level_changed_old_level'),
      labelNewLevel: t('email.level_changed_new_level'),
      newFeatures: t('email.level_changed_new_features'),
      ctaText: t('email.access_dashboard'),
      signOff: t('email.sign_off_regards'),
      teamSignature: t('email.team_signature'),
      levelFree: t('visitor.level_free'),
      levelPremium: t('visitor.level_premium'),
      levelVip: t('visitor.level_vip'),
      layout: layoutTranslations(),
    };

    const { html, text } = renderTemplate(
      React.createElement(LevelChanged, { ...data, translations })
    );
    return {
      subject: t('email.subject_level_changed'),
      html,
      text,
    };
  }

  async sendEmail(to: string, template: EmailTemplate): Promise<boolean> {
    try {
      logger.info('Sending email via API', { to, subject: template.subject });

      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to,
          subject: template.subject,
          html: template.html,
          text: template.text,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        logger.error('API Email error', new Error('API failed'), { err });
        return false;
      }

      return true;
    } catch (error) {
      logger.error('Email exception', error as Error);
      return false;
    }
  }
}

export const reactEmailService = new ReactEmailService();
export default reactEmailService;
